import Head from 'expo-router/head';
import React from 'react';
import { BRAND_DESCRIPTION, BRAND_SLOGAN, BRAND_TITLE } from '@/content/marketing/home';
import { twinForPath } from '@/lib/seo/twins';

export const SITE_URL = 'https://www.controlapag.com.br';

// Texto da marca mora em `content/marketing/home.ts` (testado em `scripts/seo-home.test.mjs`).
export const SITE_TITLE = BRAND_TITLE;

/** Descrição única do site: meta da home, Organization e WebSite. */
export const SITE_DESCRIPTION = BRAND_DESCRIPTION;

/** Frase da marca: `slogan` da Organization e texto do rodapé. */
export const SITE_SLOGAN = BRAND_SLOGAN;

type SeoHeadProps = {
  title?: string;
  description?: string;
  /** Caminho da rota, começando com "/". Vira a URL canônica e a og:url. */
  path?: string;
  /** Páginas de app (logado, transacional) não devem entrar em buscador. */
  noindex?: boolean;
  /** Objetos schema.org (JSON-LD) da rota; saem no HTML estático. */
  jsonLd?: Record<string, unknown>[];
};

/** `<` escapado para o JSON nunca fechar a tag <script> por acidente. */
const serializeJsonLd = (data: Record<string, unknown>) =>
  JSON.stringify(data).replace(/</g, '\\u003c');

/**
 * Metadados por rota no HTML estático.
 *
 * O export web é pré-renderizado: o que estiver aqui sai no arquivo servido
 * antes de qualquer JavaScript rodar, que é tudo o que buscadores e crawlers de
 * IA leem. `RootLayout` renderiza este componente sem props para dar o padrão
 * do site a toda rota, e cada tela pública o renderiza de novo com o que é
 * dela — o react-helmet fica com o mais profundo, sem duplicar tag.
 */
export const SeoHead: React.FC<SeoHeadProps> = ({
  title = SITE_TITLE,
  description = SITE_DESCRIPTION,
  path,
  noindex,
  jsonLd,
}) => {
  const url = path ? `${SITE_URL}${path}` : SITE_URL;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'}
      />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {path ? <link rel="canonical" href={url} /> : null}
      {/* Versão em markdown para agentes, só onde ela existe (`lib/seo/twins.ts`). */}
      {path && !noindex && twinForPath(path) ? (
        <link rel="alternate" type="text/markdown" href={`${SITE_URL}${twinForPath(path)!.mdPath}`} />
      ) : null}
      {jsonLd?.map((item, index) => (
        <script key={index} type="application/ld+json">
          {serializeJsonLd(item)}
        </script>
      ))}
    </Head>
  );
};
